import {ellipsis} from 'polished';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {EventHandler} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {SimpleDropdownItemWithColor} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItemWithColor';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TableCell} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCell';
import {TableCellProps} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCellTypes';
import {
  StyledTextCellContainerDiv,
  StyledTextCellValueDiv,
} from '../../../../../libs/shared/ui/src/components/tables/cells/textCell';
import {getCellTextColor} from '../../../../../libs/shared/ui/src/components/tables/helpers/tableCellHelpers';
import {Skeleton} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {emojiToDefinition} from '../../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {HighlightTypesEnum} from '../../../../models/api/highlightBaseModel';
import {TagModel} from '../../../../models/api/tagModel';
import {baseEmojiValidationMap} from '../../../../services/emojis/emojis';
import {SettingsSingleTagEditDialogContainer} from '../../../../settings/tags/singleTag/settingsSingleTagEditDialogContainer';
import {Emoji} from '../../emoji/emoji';

export interface TagCellProps extends TableCellProps<TagModel> {
  isEditTagModalOpen?: boolean;
  // @TODO: Remove onEditRowClick + onDeleteRowClick once the old AI tagging editor is removed.
  onEditRowClick?: () => void;
  onDeleteRowClick?: (arg: number[]) => void;
}

interface StyledLabelDivProps {
  $isSelected: boolean;
}

const StyledLabelDiv = styled.div<StyledLabelDivProps>`
  ${ellipsis()};
  ${(p) => getCellTextColor(p.$isSelected)}
`;

const StyledTagIcon = styled.div`
  margin-right: 8px;
`;

const StyledActionsDiv = styled.div`
  margin-left: auto;
`;

export const TagCell: FC<TagCellProps> = ({
  isSelected,
  isHovering,
  isLoading,
  size,
  settings,
  index,
  shouldShowStickyGradient,
  shouldRemoveBottomBorderRadius,
  shouldRemoveTopBorderRadius,
  shouldRemoveLeftBorderRadius,
  value,
  isEditTagModalOpen,
  onEditRowClick,
  onDeleteRowClick,
}) => {
  const shouldRenderEditAction = onEditRowClick !== undefined && onDeleteRowClick !== undefined;
  return (
    <TableCell<TagModel>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      shouldRemoveLeftBorderRadius={shouldRemoveLeftBorderRadius}
      renderLoading={() => (
        <StyledTextCellContainerDiv
          $settings={settings}
          $isHovering={false}
          $isSelected={Boolean(isSelected)}
          $index={index}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledLabelDiv $isSelected={false}>
            <Skeleton height="16px" width="140px" borderRadius="4px" scheme={settings?.loading?.style} />
          </StyledLabelDiv>
        </StyledTextCellContainerDiv>
      )}
      renderCell={(tag) => (
        <StyledTextCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
          $paddingOverride="0px 0px 0px 12px"
        >
          <StyledTagIcon>{renderTagIcon(tag, Boolean(isSelected))}</StyledTagIcon>
          <StyledTextCellValueDiv $isSelected={Boolean(isSelected)}>{tag.name}</StyledTextCellValueDiv>
          {shouldRenderEditAction && (
            <StyledActionsDiv>
              <PopoverCoordinator
                renderButton={() =>
                  isHovering &&
                  !isSelected && (
                    <IconButton
                      label={<Trans id="ai-tagging-actions">Actions</Trans>}
                      iconName="moreVertical"
                      size={VisualSizesEnum.SMALL}
                    />
                  )
                }
                renderModal={(popoverProps) =>
                  renderDropdown(popoverProps, tag, isEditTagModalOpen, onEditRowClick, onDeleteRowClick)
                }
              />
            </StyledActionsDiv>
          )}
        </StyledTextCellContainerDiv>
      )}
    />
  );
};

function renderTagIcon(tag: TagModel, isSelected: boolean) {
  switch (tag.type) {
    case HighlightTypesEnum.EMOJI: {
      const definition = emojiToDefinition(tag.highlightEmoji, baseEmojiValidationMap);
      if (!definition) {
        return null;
      }
      return <Emoji {...definition} size={16} />;
    }

    case HighlightTypesEnum.COLOR:
      return (
        <UserThemeConsumer>
          {(theme) => <Icon name="tag" color={theme.palette[tag.highlightColor].shade40} />}
        </UserThemeConsumer>
      );

    case HighlightTypesEnum.NONE:
      return (
        <UserThemeConsumer>
          {(theme) => <Icon name="tag" color={isSelected ? theme.foreground : PaletteColorsEnum.GREY} />}
        </UserThemeConsumer>
      );

    default:
      return null;
  }
}

function renderDropdown(
  popoverProps: PopoverTransferProps,
  tag: TagModel,
  isEditTagModalOpen: boolean | undefined,
  onEditRowClick: () => void,
  onDeleteRowClick: (arg: number[]) => void,
) {
  const onEditClick: EventHandler = (e) => {
    e.preventDefault(); // keep the dropdown open so that we re-render this component correctly.
    onEditRowClick();
  };

  if (isEditTagModalOpen) {
    return renderEditDialog(popoverProps, tag, onEditRowClick);
  }

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <SimpleDropdown {...popoverProps}>
      <SimpleDropdownItem onSelect={onEditClick}>
        <Trans id="ai-tagging-table-row-name-action-edit">Edit</Trans>
      </SimpleDropdownItem>
      <SimpleDropdownItemWithColor onSelect={() => onDeleteRowClick([tag.id])} color={PaletteColorsEnum.RED}>
        <Trans id="ai-tagging-table-row-name-action-delete">Delete tag from rule</Trans>
      </SimpleDropdownItemWithColor>
    </SimpleDropdown>
  );
}

function renderEditDialog(popoverProps: PopoverTransferProps, tag: TagModel, onEditRowClick: () => void) {
  const onRequestClose = () => {
    popoverProps.onRequestClose(); // Close the dropdown
    onEditRowClick(); // Reset toggle back.
  };

  return (
    <SettingsSingleTagEditDialogContainer
      tagId={tag.id}
      namespace={tag.namespace}
      onRequestClose={onRequestClose}
    />
  );
}
